import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: BACKEND_URL
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('hoa_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ConfidentialityConsentModal = ({ user, onClose, onSigned }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [signing, setSigning] = useState(false);
  const [hoaName, setHoaName] = useState('HOA');

  useEffect(() => {
    // Get HOA name
    if (user?.hoa_id) {
      api.get(`/api/hoa/${user.hoa_id}/profile`)
        .then(res => {
          setHoaName(res.data.community_name || 'HOA');
        })
        .catch(err => console.error('Error fetching HOA name:', err));
    }
  }, [user]);

  const handleSign = async () => {
    if (!acknowledged) {
      alert('You must acknowledge and agree to the terms before signing.');
      return;
    }

    setSigning(true);
    try {
      const response = await api.post('/api/confidentiality/sign', {
        consent_acknowledged: true
      });

      if (response.data.success) {
        alert('Confidentiality consent signed successfully!');
        if (onSigned) onSigned();
        onClose();
      }
    } catch (error) {
      console.error('Error signing consent:', error);
      alert('Failed to sign consent: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Confidentiality and Data-Use Agreement
        </h2>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800">
            <strong>Required:</strong> All individuals with access to HOA data must sign and comply with this agreement.
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 max-h-96 overflow-y-auto text-sm">
          <h3 className="font-bold text-lg mb-2">CONFIDENTIALITY AND DATA-USE AGREEMENT</h3>
          <p className="mb-2">For: <strong>{hoaName}</strong> / Affiliated Online Platform</p>

          <h4 className="font-semibold mt-4 mb-2">Purpose</h4>
          <p className="mb-4">
            This Agreement establishes the duty of confidentiality and responsible use of all association data and communications accessed through the HOA or its digital platforms.
          </p>

          <h4 className="font-semibold mt-4 mb-2">1. Scope of Access</h4>
          <p className="mb-4">
            This Agreement applies to any person — including board members, committee members, management personnel, volunteers, or platform users — who is granted access to association information, records, or communications.
          </p>

          <h4 className="font-semibold mt-4 mb-2">2. Definition of Confidential Information</h4>
          <p className="mb-2">"Confidential Information" includes, but is not limited to:</p>
          <ul className="list-disc ml-6 mb-4">
            <li>Owner or resident names, contact details, account balances, or correspondence.</li>
            <li>Internal board communications, legal opinions, or disciplinary matters.</li>
            <li>Vendor contracts, financial data, and strategic plans.</li>
            <li>Any data stored on or transmitted through the HOA's online systems that could reasonably be deemed private or proprietary.</li>
          </ul>

          <h4 className="font-semibold mt-4 mb-2">3. Non-Disclosure and Use Limitations</h4>
          <p className="mb-2">By signing this Agreement, I acknowledge and agree that:</p>
          <ul className="list-disc ml-6 mb-4">
            <li>I will use Confidential Information solely for official association purposes and not for personal, commercial, or political gain.</li>
            <li>I will not disclose or share Confidential Information with any person who is not authorized to receive it.</li>
            <li>I will not use information obtained through my role or platform access to intimidate, harass, or discredit another homeowner, board member, or third party.</li>
            <li>I understand that any violation of this Agreement may result in removal from office, termination of access privileges, or legal action.</li>
          </ul>

          <h4 className="font-semibold mt-4 mb-2">4. Data Protection and Retention</h4>
          <p className="mb-2">All digital data collected or stored by the Association or its platform shall:</p>
          <ul className="list-disc ml-6 mb-4">
            <li>Be limited to non-personally identifiable information except where necessary for operational purposes;</li>
            <li>Be retained only as long as required by the Association's record-retention policy and applicable law;</li>
            <li>Be handled in accordance with the Privacy Policy published on the Association's official website.</li>
          </ul>

          <h4 className="font-semibold mt-4 mb-2">5. Electronic Systems and Consent</h4>
          <p className="mb-4">
            I understand that use of electronic communications (email, portal messages, or digital voting) constitutes acknowledgment that those records may be stored and audited for legitimate association business.
          </p>

          <h4 className="font-semibold mt-4 mb-2">6. Term and Survival</h4>
          <p className="mb-4">
            This Agreement remains in effect during and after my term, employment, or platform access period. The duty of confidentiality survives termination of my position or access.
          </p>

          <h4 className="font-semibold mt-4 mb-2">7. Governing Law</h4>
          <p className="mb-4">
            This Agreement shall be governed by the laws of the State of Florida (or the state in which the Association operates).
          </p>

          <h4 className="font-semibold mt-4 mb-2">8. Acknowledgment and Signature</h4>
          <p className="mb-4">
            I acknowledge that I have read and understand this Agreement, and I agree to abide by its terms.
          </p>
        </div>

        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <div className="text-sm text-gray-700">
              <strong>I acknowledge and agree</strong> that I have read and understand this Confidentiality and Data-Use Agreement, and I agree to abide by all its terms and conditions.
            </div>
          </label>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={signing}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSign}
            disabled={!acknowledged || signing}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {signing ? 'Signing...' : 'Sign Agreement'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfidentialityConsentModal;
